import {
  Board,
  ProductionStatus,
  Shipment,
} from "../../../../reducers/uploadedBoard";
import { ShipmentsRow } from "./ShipmentsStyles";
import { FakeLink } from "../../../../constants/Styles";
import settings from "../../../../constants/settings";
import { useDownloadFileFromWebApp } from "../../../../utils/hooks/downloads";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import LoadingOverlay from "../../../LoadingOverlay";
import { useCallback, useState } from "react";
import Modal from "../../../common/Modal";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";
import Checkbox from "../../../common/Checkbox";
import Link from "next/link";
import PurchaseShippingLabel from "./PurchaseShippingLabel";
import {
  useCustomConfirm,
  useCustomOptions,
} from "../../../../utils/hooks/alerts";
import { useUpdateShipments } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/shipments";
import { ButtonVariants } from "../../../common/Button/constants";
import FlashPCBTooltip from "../../../common/FlashPCBTooltip";
import InfoIcon from "../../../common/FlashPCBTooltip/InfoIcon";
import OrderPickUpContact from "./OrderPickUpContact";

function isShipmentManufactured(shipment: Shipment): boolean {
  return shipment.production_status == ProductionStatus.MANUFACTURED;
}

export default function InternalShipmentTools({
  shipment,
}: {
  shipment: Shipment;
}) {
  const [showDeliveryTools, setShowDeliveryTools] = useState(false);
  const board = useBoardFromContext().board as Board;
  const { download, downloading } = useDownloadFileFromWebApp();
  const customConfirm = useCustomConfirm();
  const shipmentIsManufactured = isShipmentManufactured(shipment);
  const { updateManufacturedStatus, updatingManufacturedStatus } =
    useUpdateShipments();
  const customOptions = useCustomOptions();

  const handleClickManufactured = useCallback(() => {
    if (shipmentIsManufactured) {
      updateManufacturedStatus(shipment.id, false, false);
    } else {
      customOptions("Mark shipment as manufactured", [
        {
          text: "Do not notify customer",
          variant: ButtonVariants.SECONDARY,
          onSelect: () => updateManufacturedStatus(shipment.id, true, false),
        },
        {
          text: "Notify customer",
          variant: ButtonVariants.PRIMARY,
          onSelect: () => updateManufacturedStatus(shipment.id, true, true),
        },
      ]);
    }
  }, [
    customOptions,
    shipment.id,
    shipmentIsManufactured,
    updateManufacturedStatus,
  ]);

  const handleDownloadPackingSlip = () => {
    download(
      `${settings.api_url}/boards/${board.id}/shipments/${shipment.id}/packing-slip`,
    );
  };

  const closePurchaseShippingLabel = () => {
    setShowDeliveryTools(false);
  };

  const openPurchaseShippingLabel = () => {
    if (shipment.delivery_info.shipping_rate?.shippo_label_url) {
      customConfirm(
        "A shipping label was already purchased. Are you sure you want to purchase another one?",
        () => setShowDeliveryTools(true),
      );
    } else {
      setShowDeliveryTools(true);
    }
  };

  return (
    <>
      <Modal
        isOpen={showDeliveryTools}
        onRequestClose={closePurchaseShippingLabel}
      >
        <ModalContentWithCloseButton
          closeModal={closePurchaseShippingLabel}
          title={"Purchase shipping label"}
          fullScreen
        >
          <PurchaseShippingLabel
            shipment={shipment}
            onPurchase={closePurchaseShippingLabel}
          />
        </ModalContentWithCloseButton>
      </Modal>
      <LoadingOverlay loading={downloading} text={"Downloading..."} />
      <LoadingOverlay
        loading={updatingManufacturedStatus}
        text={"Updating shipment..."}
      />
      <ShipmentsRow>
        {shipment.delivery_info.shipping_address !== null && (
          <FakeLink onClick={openPurchaseShippingLabel}>
            Purchase shipping label
          </FakeLink>
        )}
        {shipment.delivery_info.pickup_info !== null && (
          <FlashPCBTooltip
            content={
              <OrderPickUpContact
                pickupInfo={shipment.delivery_info.pickup_info}
              />
            }
          >
            Order pickup info{"  "}
            <InfoIcon />
          </FlashPCBTooltip>
        )}
        {shipment.delivery_info.shipping_rate !== null &&
          shipment.delivery_info.shipping_rate.tracking_url && (
            <Link
              href={shipment.delivery_info.shipping_rate.tracking_url}
              target={"_blank"}
            >
              Track shipment
            </Link>
          )}
        {shipment.delivery_info.shipping_rate !== null &&
          shipment.delivery_info.shipping_rate.shippo_label_url && (
            <Link
              href={shipment.delivery_info.shipping_rate.shippo_label_url}
              target={"_blank"}
            >
              Shipping label
            </Link>
          )}
        <FakeLink onClick={handleDownloadPackingSlip}>Packing slip</FakeLink>
        <Checkbox
          checked={shipmentIsManufactured}
          onChange={handleClickManufactured}
          label={"Manufactured"}
        />
      </ShipmentsRow>
    </>
  );
}
